import styled, { keyframes } from "styled-components";

import { colors, components, fonts, radius, spacing, transitions } from "@/styles/design-system";

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideDown = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(24px) scale(0.97); }
`;

/* ── Overlay ── */

export const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity 0.2s ease;
`;

/* ── Modal Container ── */

export const ModalContainer = styled.div<{ $closing?: boolean }>`
  position: relative;
  width: 92%;
  max-width: 520px;
  /* Static gradient — see components.modal.background in
     src/styles/design-system.ts. No more backdrop-filter blur. */
  background: ${components.modal.background};
  border: ${components.modal.border};
  border-radius: ${components.modal.borderRadius};
  box-shadow: ${components.modal.boxShadow};
  padding: ${spacing["6xl"]} ${spacing["5xl"]} ${spacing["5xl"]};
  animation: ${({ $closing }) => ($closing ? slideDown : slideUp)} 0.25s ease both;

  /* phone: trim the padding so the CTA + login link sit higher */
  @media (max-width: 600px) {
    padding: 28px 20px 24px;
  }
`;

/* ── Close Button ── */

export const CloseButton = styled.button`
  position: absolute;
  top: ${spacing.xl};
  right: ${spacing.xl};
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(235, 194, 142, 0.2);
  border: 1px solid ${colors.border.default};
  color: ${colors.text.link};
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${transitions.normal};
  line-height: 1;
  padding: 0;

  &:hover {
    background: rgba(235, 194, 142, 0.35);
    border-color: ${colors.border.inputFocus};
    color: #fff;
  }
`;

/* ── Title ── */

export const Title = styled.h2`
  font-family: ${fonts.heading};
  font-size: 2rem;
  font-weight: 600;
  color: ${colors.text.heading};
  text-align: center;
  margin: 0 0 ${spacing.sm} 0;

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

/* ── Subtitle ── */

export const Subtitle = styled.p`
  font-family: ${fonts.body};
  font-size: 0.875rem;
  color: ${colors.text.body};
  text-align: center;
  margin: 0 0 ${spacing["3xl"]} 0;
  line-height: 1.5;

  @media (max-width: 600px) {
    margin-bottom: ${spacing.xl};
  }
`;

/* ── Advantage Cards Grid ── */

export const AdvantagesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.lg};
  margin-bottom: ${spacing["4xl"]};

  @media (max-width: 600px) {
    gap: ${spacing.sm};
    margin-bottom: ${spacing["2xl"]};
  }
`;

export const AdvantageCard = styled.div`
  background: ${colors.surface.glass};
  border: 1px solid ${colors.border.light};
  border-radius: ${radius.xl};
  padding: ${spacing["2xl"]} ${spacing.xl};
  text-align: center;
  transition: all ${transitions.normal};

  &:hover {
    background: rgba(235, 194, 142, 0.06);
    border-color: ${colors.border.default};
  }

  /* phone: tighter cards — the second description is hidden so the box
     only needs room for the icon + title */
  @media (max-width: 600px) {
    padding: ${spacing.lg} ${spacing.md};
  }
`;

export const AdvantageIcon = styled.div`
  width: 36px;
  height: 36px;
  margin: 0 auto ${spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.85;

  svg {
    width: 28px;
    height: 28px;
  }

  @media (max-width: 600px) {
    width: 28px;
    height: 28px;
    margin-bottom: ${spacing.sm};

    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

export const AdvantageTitle = styled.div`
  font-family: ${fonts.body};
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: ${colors.text.heading};
  margin-bottom: ${spacing.sm};

  /* phone: the description is hidden, so the title stands alone — use Raleway
     (body font) instead of the serif heading so it reads as a clean label */
  @media (max-width: 600px) {
    font-family: ${fonts.body};
    font-size: 0.95rem;
    font-weight: 400;
    margin-bottom: 0;
  }
`;

export const AdvantageDesc = styled.div`
  font-size: 0.75rem;
  color: ${colors.text.muted};
  line-height: 1.45;

  /* phone: drop the secondary description entirely to save vertical space */
  @media (max-width: 600px) {
    display: none;
  }
`;

/* ── Primary CTA Button ── */

export const PrimaryButton = styled.button`
  display: block;
  width: 100%;
  padding: 15px 28px;
  background: linear-gradient(135deg, ${colors.gold.light} 0%, #ffebb5 100%);
  color: ${colors.brown.darkest};
  font-family: ${fonts.body};
  font-size: 0.9375rem;
  font-weight: 700;
  border: none;
  border-radius: ${radius.md};
  cursor: pointer;
  box-shadow: 0 0 32px rgba(186, 144, 92, 0.4);
  transition: all ${transitions.normal};
  letter-spacing: 0.02em;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 40px rgba(186, 144, 92, 0.6);
  }

  &:active {
    transform: translateY(0);
  }
`;

/* ── Login Link ── */

export const LoginLink = styled.div`
  text-align: center;
  margin-top: ${spacing["2xl"]};
  font-size: 0.8125rem;
  color: ${colors.text.muted};

  a,
  button {
    color: ${colors.accent.blue};
    text-decoration: none;
    font-weight: 500;
    transition: color ${transitions.fast};
    background: none;
    border: none;
    cursor: pointer;
    font-size: inherit;
    font-family: inherit;
    padding: 0;

    &:hover {
      color: ${colors.accent.blueLight};
    }
  }
`;
